//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

import { useTermsDialog } from "@/composables/ui/dialogs/useTermsDialog";

/**
 * Login Page Composable
 *
 * Manages authentication check and navigation ONLY.
 * Laboratory branding handled by useLaboratoryBranding.
 * Terms handled by useTermsDialog.
 *
 * Responsibilities:
 * - Check SSO authentication status
 * - Navigate to app if authenticated
 * - Manage loading state during auth check
 * - Coordinate branding and terms initialization
 *
 * Architecture:
 * - Uses useLaboratoryBranding for logo/name (with single locale watcher)
 * - Uses useTermsDialog for terms of use management
 * - Focuses solely on authentication flow
 *
 * @param key - Unique key for state isolation (default: "loginPage")
 */
export const useLoginPage = (key = "loginPage") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  // Auth
  const { init, isAuthenticated } = useAuth();

  // Localization
  const localePath = useLocalePath();

  // Error Handling
  const { handleError } = useErrorBoundary();

  // Laboratory branding (handles its own locale watching)
  const branding = useLaboratoryBranding();

  // Terms dialog (handles its own state)
  const termsDialog = useTermsDialog(`${key}.terms`);

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  const isCheckingAuth = useState<boolean>(`${key}.isCheckingAuth`, () => true);
  const isInitialized = useState<boolean>(`${key}.isInitialized`, () => false);

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  /**
   * Laboratory name with fallback
   */
  const labName = computed(() => branding.laboratoryName.value || "");

  /**
   * Check if we have all required data
   */
  const hasLaboratoryData = computed(() => branding.hasData.value);

  /**
   * Check if page is ready for display
   */
  const isReady = computed(() => branding.hasData.value && !isCheckingAuth.value);

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Initialize login page
   *
   * Purpose:
   * - Check SSO authentication status
   * - Initialize laboratory branding
   * - Load terms of use
   * - Redirect to app if authenticated
   */
  const initialize = async (): Promise<void> => {
    if (isInitialized.value) return;

    isInitialized.value = true;
    isCheckingAuth.value = true;

    try {
      // Run in parallel: auth check + branding initialization + terms loading
      await Promise.all([init("check-sso"), branding.initialize(), termsDialog.loadTerms()]);

      // Redirect if authenticated
      if (isAuthenticated.value) {
        await navigateTo(localePath("/app"), { replace: true });
        return;
      }
    } catch (error) {
      handleError(error, {
        operation: "initializeLoginPage",
        showToast: true,
      });
    } finally {
      if (!isAuthenticated.value) {
        isCheckingAuth.value = false;
      }
    }
  };

  /**
   * Set terms acceptance
   *
   * @param value - Accept or reject terms
   */
  const setAgreeToTerms = (value: boolean): void => {
    termsDialog.agreeToTerms.value = value;
  };

  //
  // ============================================================================
  // RETURN PUBLIC API
  // ============================================================================
  //

  return {
    // State (readonly)
    isCheckingAuth: readonly(isCheckingAuth),

    // From branding composable
    laboratoryLogoUrl: branding.logoUrl,
    laboratoryName: branding.laboratoryName,
    labName,
    hasLaboratoryData,

    // From terms dialog
    termsDialog,
    termsOfUse: termsDialog.termsContent,
    agreeToTerms: termsDialog.agreeToTerms,

    // Computed
    isReady,

    // Methods
    initialize,
    setAgreeToTerms,
  };
};
